import { 
  Accordion,
  AccordionButton, 
  AccordionItem, 
  AccordionPanel, 
  Box, 
  Flex, 
  HStack, 
  Image, 
  Text,
  useDisclosure
} from "@chakra-ui/react";
import { Children } from "react";
import { Swiper } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { 
  IconBarcelonaMinimap, 
  IconChevron, 
  IconSpaMinimap, 
  IconSilverstoneMinimap, 
  IconZandvoortMinimap, 
  IconAustinMinimap, 
  IconImolaMinimap, 
  IconSakhirMinimap, 
  IconLusailMinimap, 
  IconBudapestMinimap, 
  IconSpielbergMinimap, 
  IconMontrealMinimap, 
  IconSuzukaMinimap, 
  IconMelbourneMinimap, 
  IconMexicoCityMinimap, 
  IconMagnifyingGlass
} from "icons";
import { CountdownTimer } from "components/CountdownTimer/CountdownTimer";
import RaceMinimap from "components/Races2/RaceMinimap";
import { RRaceStatus } from "components";
import { RaceStatus } from "types";
import RaceThumbnailModal from "components/Races2/RaceThumbnail/RaceThumbnailModal";

interface TrackAccordionProps {
  children: React.ReactNode;
  race: string;
  raceStatus?: string;
  countdownTime: string | null;
  isJoinTab: boolean;
  isResultsTab?: boolean;
  defaultIndex?: number[];
}

type RaceMinimapType = {
  [key: string]: React.ElementType;
};

export const RaceMinimapList: RaceMinimapType = {
  Belgium: IconSpaMinimap,
  Spain: IconBarcelonaMinimap,
  'United Kingdom': IconSilverstoneMinimap,
  Netherlands: IconZandvoortMinimap,
  USA: IconAustinMinimap,
  Italy: IconImolaMinimap,
  Bahrain: IconSakhirMinimap,
  Qatar: IconLusailMinimap,
  Hungary: IconBudapestMinimap,
  Austria: IconSpielbergMinimap,
  Canada: IconMontrealMinimap,
  Japan: IconSuzukaMinimap,
  Australia: IconMelbourneMinimap,
  'Mexico City': IconMexicoCityMinimap,
  Texas: IconAustinMinimap,
};

// Add this mapping for track backgrounds
export const trackBackgrounds: Record<string, string> = {
  'United Kingdom': 'silverstone.webp',
  'Australia': 'australia.webp',
  'Texas': 'usa.webp',
  'Italy': 'imola.webp',
  'Qatar': 'qatar.webp',
  'Bahrain': 'bahrain.webp',
  'Japan': 'japan.webp',
  'Austria': 'austria.webp',
  'Netherlands': 'netherlands.webp',
  'Mexico City': 'mexico.webp',
  'Canada': 'canada.webp',
  'Belgium': 'spa.webp',
  'Spain': 'spain.webp',
  'Azerbaijan': 'azerbaijan.webp',
  'Brazil': 'brazil.webp',
  'China': 'china.webp',
  'Hungary': 'hungary.webp',
  'Turkey': 'turkey.webp',
  'United States': 'usa.webp',
  'Las Vegas': 'usa.webp',
  // Add more tracks as needed
};

export const trackFlags: Record<string, string> = {
  'United Kingdom': 'united-kingdom.svg',
  'Australia': 'australia.svg',
  'Texas': 'united-states-of-america.svg',
  'Italy': 'italy.svg',
  'Qatar': 'qatar.svg',
  'Bahrain': 'bahrain.svg',
  'Japan': 'japan.svg',
  'Austria': 'austria.svg',
  'Netherlands': 'netherlands.svg',
  'Mexico City': 'mexico.svg',
  'Canada': 'canada.svg',
  'Belgium': 'belgium.svg',
  'Spain': 'spain.svg',
  'Azerbaijan': 'azerbaijan.svg',
  'Brazil': 'brazil.svg',
  'China': 'china.svg',
  'Hungary': 'hungary.svg',
  'Turkey': 'turkey.svg',
  'United States': 'united-states-of-america.svg',
  'Las Vegas': 'united-states-of-america.svg',
}

// Add this mapping for track descriptions
export const trackDescriptions: Record<string, string> = {
  'United Kingdom': 'Old airfield with classic sequences of fast turns.',
  'Australia': 'Flowing but bumpy parkland street circuit.',
  'Texas': 'Wide hilly circuit with a mix of different turns.',
  'Italy': 'Historic and challenging, with iconic corners.',
  'Qatar': 'Hot desert circuit with flowing medium and fast turns.',
  'Bahrain': 'Fast desert circuit, with many medium-slow turns.',
  'Japan': 'Circuit sweeps through long sequences of medium and fast turns.',
  'Austria': 'Narrow mountain circuit with many fast turns.',
  'Netherlands': 'Rollercoaster circuit by the beach with many fast turns.',
  'Azerbaijan': 'Tight city circuit with one of the longest straights.',
  'Belgium': 'Long fast blast around this forest circuit in changeable weather.',
  'Brazil': 'Hillside circuit with slow and medium turns.',
  'Canada': 'Artificial island stop-go circuit with many slow turns.',
  'China': 'Modern technical circuit with one very long straight.',
  'Hungary': 'A twisting valley circuit with many medium speed turns.',
  'Mexico City': 'High-altitude plateau circuit with long straights.',
  'Monaco': 'Tight city street circuit with continuous slow turns.',
  'Saudi Arabia': 'Desert city street circuit with many fast turns.',
  'Singapore': 'Humid city track with short straights and slow turns.',
  'South Korea': 'Fast circuit with many medium and fast turns.',
  'Spain': 'Fast circuit with many medium and fast turns.',
  'Turkey': 'Rollercoaster circuit with many fast turns.',
  'United States': 'Fast circuit with many medium and fast turns.',
  // Add more descriptions as needed
};

export const cardGradients: Record<string, string> = {
  'Australia': "linear-gradient(5deg, rgba(26, 71, 184, 0) 69.1%, rgba(26, 71, 184, 0.3) 79.95%, rgba(26, 71, 184, 0.8) 96.24%)",
  'Austria': "linear-gradient(5deg, rgba(249, 57, 57, 0) 69.1%, rgba(249, 57, 57, 0.3) 79.95%, rgba(249, 57, 57, 0.8) 96.24%)",
  'Bahrain': "linear-gradient(5deg, rgba(249, 57, 57, 0) 69.1%, rgba(249, 57, 57, 0.3) 79.95%, rgba(249, 57, 57, 0.8) 96.24%)",
  'Belgium': "linear-gradient(5deg, rgba(255, 218, 44, 0) 69.1%, rgba(255, 218, 44, 0.3) 79.95%, rgba(255, 218, 44, 0.8) 96.24%)",
  'Canada': "linear-gradient(5deg, rgba(249, 57, 57, 0) 69.1%, rgba(249, 57, 57, 0.3) 79.95%, rgba(249, 57, 57, 0.8) 96.24%)",
  'Italy':  "linear-gradient(5deg, rgba(36, 159, 88, 0) 69.1%, rgba(36, 159, 88, 0.3) 79.95%, rgba(36, 159, 88, 0.8) 96.24%)",
  'Japan': "linear-gradient(5deg, rgba(249, 57, 57, 0) 69.1%, rgba(249, 57, 57, 0.3) 79.95%, rgba(249, 57, 57, 0.8) 96.24%)",
  'Mexico City': "linear-gradient(5deg, rgba(36, 159, 88, 0) 69.1%, rgba(36, 159, 88, 0.3) 79.95%, rgba(36, 159, 88, 0.8) 96.24%)",
  'Netherlands': "linear-gradient(5deg, rgba(255, 102, 0, 0) 69.1%, rgba(255, 102, 0, 0.3) 79.95%, rgba(255, 102, 0, 0.8) 96.24%)",
  'Spain': "linear-gradient(5deg, rgba(255, 218, 44, 0) 69.1%, rgba(255, 218, 44, 0.3) 79.95%, rgba(255, 218, 44, 0.8) 96.24%)",
  'Texas': "linear-gradient(5deg, rgba(249, 57, 57, 0) 69.1%, rgba(249, 57, 57, 0.3) 79.95%, rgba(249, 57, 57, 0.8) 96.24%)",
  'United Kingdom': "linear-gradient(5deg, rgba(26, 71, 184, 0) 69.1%, rgba(26, 71, 184, 0.3) 79.95%, rgba(26, 71, 184, 0.8) 96.24%)",
  'Qatar': "linear-gradient(5deg, rgba(249, 57, 57, 0) 69.1%, rgba(249, 57, 57, 0.3) 79.95%, rgba(249, 57, 57, 0.8) 96.24%)",
  'Las Vegas': "linear-gradient(5deg, rgba(249, 57, 57, 0) 69.1%, rgba(249, 57, 57, 0.3) 79.95%, rgba(249, 57, 57, 0.8) 96.24%)",
}

const TrackAccordion = ({ children, race, raceStatus, countdownTime, isJoinTab, isResultsTab, defaultIndex }: TrackAccordionProps) => {
  const childrenCount = Children.count(children);
  const isLive = (raceStatus === 'live' && countdownTime && new Date(countdownTime) < new Date())
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <Accordion 
      allowMultiple 
      variant="trackAccordion"
      defaultIndex={defaultIndex}
    >
      <AccordionItem 
        border="none"
        borderRadius="8px"
      >
        {({ isExpanded }) => (
          <>
            <AccordionButton
              width="full"
              justifyContent="space-between"
              alignItems={{ base: 'center', md: 'center' }}
              height="114px"
              borderTopRadius="8px"
              background={`linear-gradient(90deg, rgba(21, 21, 23, 1) 0%, rgba(21, 21, 23, 1) 30%, transparent 100%), url('/images/track-backgrounds/${trackBackgrounds[race] || 'default.webp'}')`}
              flexDirection={{ base: 'row', md: 'row' }}
              backgroundSize="cover"
              backgroundPosition="right"
              backgroundRepeat="no-repeat"
              position="relative"
              _hover={{
                background: "linear-gradient(90deg, rgba(21, 21, 23, 1) 0%, rgba(21, 21, 23, 1) 30%, transparent 100%), url('/images/track-backgrounds/${trackBackgrounds[race] || 'default.webp'}')",
                backgroundSize: "cover",
                backgroundPosition: "right",
                backgroundRepeat: "no-repeat"
              }}
            >
              <Flex 
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                margin="auto"
                zIndex={0}
                background="linear-gradient(90deg, #151517 40%, rgba(21, 21, 23, 0.2) 100%)"
                width="100%"
                height="100%"
                borderTopRadius="8px"
              />

              <Flex 
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                margin="auto"
                zIndex={1}
                background={cardGradients[race]}
                width="100%"
                height="100%"
                borderTopRadius="8px"
              />
                
              <HStack
                zIndex={2}
                gap={{ base: '3', md: '4' }}
                w="full"
                borderRadius="8px"
              >
                <Flex
                  background="white.10"
                  p="4"
                  borderRadius="4px"
                  transition="all 0.2s ease"
                  _hover={{
                    background: 'white.20',
                  }}
                  alignItems="center"
                  alignSelf="stretch"
                >
                  <IconChevron 
                    boxSize={4} 
                    transform={!isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                    transition="all 0.2s ease"
                  />
                </Flex>

                <Flex
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={1}
                  alignItems={{ base: 'flex-start', md: 'center' }}
                  w="full"
                  justifyContent="space-between"
                >
                  <Flex
                    flexDirection="column"
                    alignItems="flex-start"
                    maxW={{ base: '100%', md: '70%' }}
                    gap={1}
                  >
                    <Flex
                      alignItems="center"
                      gap={2}
                    >
                      <Image 
                        src={`/images/flags/${trackFlags[race]}`}
                        alt={`${race} Flag`}
                        width={{ base: '22px', md: '42px' }}
                        height={{ base: '16px', md: '30px' }}
                      />
                      <Text
                        fontSize={{ base: '16px', md: '40px' }}
                        lineHeight={{ base: '16px', md: '40px' }}
                        fontWeight="400"
                        color="white.95"
                        fontFamily="heading"
                        textTransform="uppercase"
                        textAlign="left"
                      >
                        {race}
                      </Text>
                    </Flex>
                    <Text
                      fontSize={{ base: '10px', md: '14px' }}
                      lineHeight={{ base: '12px', md: '18px' }}
                      fontWeight="400"
                      color="white.60"
                      textTransform="uppercase"
                      fontFamily="heading"
                      textAlign="left"
                    >
                      {trackDescriptions[race] || 'A challenging racing circuit'}
                    </Text>
                  </Flex>

                  {!isResultsTab && (
                    <Flex
                      gap="2"
                      alignItems="center"
                    >
                      <Text
                        fontSize={{ base: '12px', md: '1.125rem' }}
                        lineHeight={{ base: '14px', md: '22px' }}
                        fontWeight="600"
                        color="treasuredTeal.100"
                        textTransform="uppercase"
                        marginRight={isLive ? '1rem': '0'}
                      >
                        {isJoinTab ? 'Entries close in' :
                          isLive ? <RRaceStatus status={raceStatus as RaceStatus} />
                            : `Race${childrenCount > 1 ? 's' : ''} start${childrenCount > 1 ? '' : 's'} in`}
                      </Text>
                      {!isLive &&
                        <CountdownTimer
                          timeLeft={countdownTime || "2025-01-24T12:00:00Z"}
                        />}
                    </Flex>
                  )}
                </Flex>
              </HStack>

              <Flex
                flexDirection={{ base: 'column', md: 'row' }}
                alignItems="center"
                gap={4}
                ml="4"
                zIndex={10}
              >
                <Flex
                  w="64px"
                  h="64px"
                  background="linear-gradient(225deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%)"
                  borderRadius="8px"
                  border="2px solid rgba(255, 255, 255, 0.2)"
                  p="1"
                  position="relative"
                >
                  <RaceMinimap
                    race={race}
                    width="100%"
                    height="100%"
                  />
                  <Box
                    pos="absolute"
                    top="2px"
                    right="2px" 
                    margin="auto"
                    zIndex={10}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen();
                    }}
                  >
                    <IconMagnifyingGlass />
                  </Box>
                </Flex>
                <RaceThumbnailModal
                  title={race}
                  difficulty={1}
                  isOpen={isOpen}
                  onClose={onClose}
                  tier="race"
                />
              </Flex>

            </AccordionButton>
            <AccordionPanel 
              overflow="auto"
              py="2"
              px="4" 
              borderBottomRadius="8px"
              background="darkVoid.100"
              pb={{ base: '1rem', md: '1rem' }}
            >
              <Swiper
                modules={[Pagination, Navigation]}
                slidesPerView={1}
                spaceBetween={10}
                navigation={{
                  enabled: childrenCount > 1,
                  prevEl: '.swiper-button-prev-custom',
                  nextEl: '.swiper-button-next-custom',
                }}
                pagination={{
                  enabled: childrenCount > 1,
                  clickable: false,
                  type: 'progressbar',
                  horizontalClass: 'swiper-pagination-progressbar-track',
                }}
                style={{
                  position: 'relative',
                  height: window.innerWidth < 1024 ? childrenCount > 1 ? '205px' : '205px' : 'auto',
                  paddingBottom: '15px', 
                  paddingTop: '0px'
                }}
                breakpoints={{
                  320: {
                    slidesPerView: 1,
                    navigation: {
                      enabled: childrenCount > 1
                    },
                    pagination: {
                      enabled: childrenCount > 1,
                      clickable: true,
                      type: 'progressbar'
                    }
                  },
                  768: {
                    slidesPerView: 2.5,
                    navigation: {
                      enabled: childrenCount > 1
                    },
                    pagination: {
                      enabled: childrenCount > 1,
                      clickable: true
                    }
                  },
                  1024: {
                    slidesPerView: 2.5,
                    navigation: {
                      enabled: childrenCount > 1
                    },
                    pagination: {
                      enabled: childrenCount > 1,
                      clickable: true
                    }
                  },
                  1280: {
                    slidesPerView: 3.5,
                    navigation: {
                      enabled: childrenCount > 1
                    },
                    pagination: {
                      enabled: childrenCount > 1,
                      clickable: true
                    }
                  },
                }}
              >
                {children}
                <Flex
                  className="swiper-pagination"
                  position="absolute"
                  bottom="0"
                  height="4px"
                  zIndex={1}
                />
                {childrenCount > 1 && (
                  <>
                    <Flex
                      className="swiper-button-prev-custom"
                      position="absolute"
                      left="10px"
                      top="50%"
                      transform="translateY(-50%)"
                      zIndex={2}
                      w="40px"
                      h="30px"
                      bg="rgba(255, 255, 255, 0.5)"
                      borderRadius="xl"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      _hover={{ bg: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <IconChevron 
                        __css={{
                          path: {
                            fill: 'black.100'
                          }
                        }}
                        boxSize={4} 
                        transform="rotate(180deg)"
                      />
                    </Flex>

                    <Flex
                      className="swiper-button-next-custom"
                      position="absolute"
                      right="10px"
                      top="50%"
                      transform="translateY(-50%)"
                      zIndex={2}
                      w="40px"
                      h="30px"
                      bg="rgba(255, 255, 255, 0.5)"
                      borderRadius="xl"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      _hover={{ bg: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <IconChevron 
                        __css={{
                          path: {
                            fill: 'black.100'
                          }
                        }}
                        boxSize={4} 
                      />
                    </Flex>
                  </>
                )}
              </Swiper>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};

export default TrackAccordion;